import { useState, useCallback, useRef } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { DocumentCarouselFragment } from "../../__generated__/graphql";
import CarouselItemPreview from "./CarouselItemPreview";

interface DocumentCarouselProps {
  carouselItems: DocumentCarouselFragment[];
  title: string;
}

export default function DocumentCarousel({
  carouselItems,
  title,
}: DocumentCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  const goToSlide = useCallback(
    (index: number) => {
      if (index >= 0 && index < carouselItems.length) {
        setCurrentIndex(index);
      }
    },
    [carouselItems.length]
  );

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) =>
      prev === 0 ? carouselItems.length - 1 : prev - 1
    );
  }, [carouselItems.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) =>
      prev === carouselItems.length - 1 ? 0 : prev + 1
    );
  }, [carouselItems.length]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        goToPrevious();
      } else if (e.key === "ArrowRight") {
        goToNext();
      }
    },
    [goToPrevious, goToNext]
  );

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!touchStartX.current || !touchEndX.current) return;

    const distance = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50;

    if (distance > minSwipeDistance) {
      goToNext();
    } else if (distance < -minSwipeDistance) {
      goToPrevious();
    }

    touchStartX.current = null;
    touchEndX.current = null;
  }, [goToNext, goToPrevious]);

  if (carouselItems.length === 0) {
    return null;
  }

  const currentItem = carouselItems[currentIndex];

  return (
    <div
      className="flex flex-col w-full h-full relative"
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      {/* Main carousel content */}
      <div
        className="flex-1 flex items-center justify-center w-full h-full min-h-0 relative overflow-hidden"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Previous button */}
        {carouselItems.length > 1 && (
          <button
            onClick={goToPrevious}
            className="absolute left-4 z-10 p-2 rounded-full bg-dark/80 hover:bg-dark border border-input transition-colors"
            aria-label="Previous slide"
          >
            <ChevronLeftIcon className="w-6 h-6 text-primary" />
          </button>
        )}

        {/* Slide container */}
        <div className="w-full h-full flex items-center justify-center">
          <CarouselItemPreview
            carouselItem={currentItem}
            title={title}
            index={currentIndex}
            total={carouselItems.length}
          />
        </div>

        {/* Next button */}
        {carouselItems.length > 1 && (
          <button
            onClick={goToNext}
            className="absolute right-4 z-10 p-2 rounded-full bg-dark/80 hover:bg-dark border border-input transition-colors"
            aria-label="Next slide"
          >
            <ChevronRightIcon className="w-6 h-6 text-primary" />
          </button>
        )}
      </div>

      {/* Dots navigation */}
      {carouselItems.length > 1 && (
        <div className="flex justify-center items-center gap-2 py-4">
          {carouselItems.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`transition-all duration-300 rounded-full ${
                index === currentIndex
                  ? "w-3 h-3 bg-accent"
                  : "w-2 h-2 bg-tertiary opacity-50 hover:opacity-75"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
